import React, {useCallback, useState} from 'react';
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
} from '@mui/material';
import PublicRoundedIcon from '@mui/icons-material/PublicRounded';
import {CM_COLLECTION_METHOD_OPTIONS} from '../collectionTaskPayload';
import {isLikelyUrl, normalizeUrlInput, parseUrlLines} from '../../../utils/sourceImportHelpers';
import InheritedClassificationDisplay from './InheritedClassificationDisplay';

/**
 * Dedicated "Add Material" dialog for the URL entry point.
 * Calls onImport with {sourceKind:'url', urls, title, collectionMethod, classificationLevel, distribution, compartment}.
 * Classification is inherited from the parent Collection Task and is not editable here.
 */
export default function CollectionMaterialUrlImportDialog({
    open,
    onClose,
    onImport,
    busy = false,
    initialClassificationLevel = 'U',
    initialDistribution = [],
    initialCompartment = [],
}) {
    const [urlText, setUrlText] = useState('');
    const [title, setTitle] = useState('');
    const [collectionMethod, setCollectionMethod] = useState('OSINT');
    const [error, setError] = useState(null);

    const inheritedLevel = initialClassificationLevel || 'U';
    const inheritedDistribution = Array.isArray(initialDistribution) ? initialDistribution : [];
    const inheritedCompartment = Array.isArray(initialCompartment) ? initialCompartment : [];

    const resetState = useCallback(() => {
        setUrlText('');
        setTitle('');
        setCollectionMethod('OSINT');
        setError(null);
    }, []);

    const handleClose = useCallback(() => {
        if (busy) return;
        resetState();
        onClose?.();
    }, [busy, onClose, resetState]);

    const handleSubmit = useCallback(async () => {
        setError(null);
        const urls = parseUrlLines(urlText);
        if (!urls.length) {
            setError('Enter at least one URL.');
            return;
        }
        const invalid = urls.filter((url) => !isLikelyUrl(url));
        if (invalid.length) {
            setError(`Not a valid http(s) URL: ${invalid[0]}`);
            return;
        }
        try {
            // One material per URL (first URL if multiple — create sequentially from caller).
            await onImport?.({
                sourceKind: 'url',
                urls: urls.map(normalizeUrlInput),
                title: title.trim() || undefined,
                collectionMethod,
                classificationLevel: inheritedLevel,
                distribution: inheritedDistribution.length ? inheritedDistribution : undefined,
                compartment: inheritedCompartment.length ? inheritedCompartment : undefined,
            });
            resetState();
            onClose?.();
        } catch (e) {
            setError(e?.message || 'Import failed');
        }
    }, [
        collectionMethod,
        inheritedCompartment,
        inheritedDistribution,
        inheritedLevel,
        onClose,
        onImport,
        resetState,
        title,
        urlText,
    ]);

    const submitDisabled = busy || !urlText.trim();

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
            <DialogTitle>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <PublicRoundedIcon fontSize="small" color="action"/>
                    <span>Add Collected Material: URL</span>
                </Stack>
            </DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2}>
                    <TextField
                        label="URLs"
                        placeholder={'https://example.com/article'}
                        value={urlText}
                        onChange={(e) => setUrlText(e.target.value)}
                        fullWidth
                        multiline
                        minRows={3}
                        autoFocus
                        helperText="Paste one or more web page URLs (one per line). Each URL becomes a Collected Material."
                        InputProps={{
                            startAdornment: (
                                <Box component="span" sx={{mr: 1, display: 'flex', alignItems: 'flex-start', pt: 1}}>
                                    <PublicRoundedIcon fontSize="small" color="action"/>
                                </Box>
                            ),
                        }}
                    />

                    <TextField
                        size="small"
                        label="Title (optional)"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        fullWidth
                    />

                    <FormControl size="small" fullWidth>
                        <InputLabel id="cm-url-import-method-label">Collection Method</InputLabel>
                        <Select
                            labelId="cm-url-import-method-label"
                            label="Collection Method"
                            value={collectionMethod}
                            onChange={(e) => setCollectionMethod(e.target.value)}
                        >
                            {CM_COLLECTION_METHOD_OPTIONS.map((method) => (
                                <MenuItem key={method} value={method}>{method}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <InheritedClassificationDisplay
                        level={inheritedLevel}
                        distribution={inheritedDistribution}
                        compartment={inheritedCompartment}
                    />
                </Stack>

                {error && (
                    <Alert severity="error" sx={{mt: 2}}>{error}</Alert>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={busy}>Cancel</Button>
                <Button onClick={handleSubmit} variant="contained" disabled={submitDisabled}>
                    {busy ? 'Importing…' : 'Add material'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
